import { useEffect, useRef, useState } from "react";

export interface UseViewportVisibilityOptions {
  /**
   * The percentage of the element that must be visible to trigger the visibility state.
   * @default 0.5 (50%)
   */
  threshold?: number;

  /**
   * The root margin for the intersection observer.
   * Useful for triggering visibility before the element enters the viewport.
   * @default '0px'
   */
  rootMargin?: string;

  /**
   * Whether to only trigger once when the element becomes visible.
   * If true, the element will remain visible even after scrolling out of view.
   * @default false
   */
  triggerOnce?: boolean;

  /**
   * Whether the hook is enabled. Useful for conditional visibility detection.
   * @default true
   */
  enabled?: boolean;
}

export interface UseViewportVisibilityReturn<T extends HTMLElement = HTMLElement> {
  /**
   * Ref to attach to the element you want to observe
   */
  ref: React.RefObject<T | null>;

  /**
   * Whether the element is currently visible in the viewport
   */
  isVisible: boolean;

  /**
   * Whether the element has ever been visible (useful for one-time animations)
   */
  hasBeenVisible: boolean;

  /**
   * The current intersection ratio (0-1)
   */
  intersectionRatio: number;
}

/**
 * Detects if the current device is mobile/tablet based on screen width
 * Uses consistent breakpoints: mobile < 768px, tablet 768px-1279px, desktop >= 1280px
 */
function getDeviceType() {
  if (typeof window === "undefined") return "desktop";

  const width = window.innerWidth;
  if (width < 768) return "mobile";
  if (width < 1280) return "tablet";
  return "desktop";
}

export function useViewportVisibility<T extends HTMLElement = HTMLElement>(
  options: UseViewportVisibilityOptions = {},
): UseViewportVisibilityReturn<T> {
  const {
    threshold = 0.5,
    rootMargin = "0px",
    triggerOnce = false,
    enabled = true,
  } = options;

  const elementRef = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const [intersectionRatio, setIntersectionRatio] = useState(0);

  useEffect(() => {
    const element = elementRef.current;

    if (!element || !enabled) {
      return;
    }

    // Fallback for browsers without IntersectionObserver support
    if (!window.IntersectionObserver) {
      console.warn("IntersectionObserver is not supported in this browser");
      const fallbackUpdate = () => {
        setIsVisible(true);
        setHasBeenVisible(true);
        setIntersectionRatio(1);
      };
      // Use setTimeout to avoid direct setState in useEffect
      const timeoutId = setTimeout(fallbackUpdate, 0);
      return () => clearTimeout(timeoutId);
    }

    // Get device-specific threshold
    const deviceType = getDeviceType();
    const deviceThreshold =
      deviceType === "mobile"
        ? Math.min(threshold, 0.1)
        : deviceType === "tablet"
          ? Math.min(threshold, 0.2)
          : threshold;

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (!entry) return;

      const { intersectionRatio: ratio, isIntersecting } = entry;
      const visible = isIntersecting && ratio >= deviceThreshold;

      setIntersectionRatio(ratio);

      if (visible) {
        setIsVisible(true);
        setHasBeenVisible(true);

        // Disconnect observer if triggerOnce is enabled
        if (triggerOnce) {
          observer.disconnect();
        }
      } else if (!triggerOnce) {
        setIsVisible(false);
      }
    };

    // Create and start observing
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: deviceThreshold,
      rootMargin,
    });

    observer.observe(element);

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce, enabled]);

  // Reset states when disabled
  useEffect(() => {
    if (!enabled) {
      const resetStates = () => {
        setIsVisible(false);
        setHasBeenVisible(false);
        setIntersectionRatio(0);
      };
      // Use setTimeout to avoid direct setState in useEffect
      const timeoutId = setTimeout(resetStates, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [enabled]);

  return {
    ref: elementRef,
    isVisible,
    hasBeenVisible,
    intersectionRatio,
  };
}

/**
 * Simplified hook for basic viewport visibility detection
 * Uses conservative settings suitable for most use cases
 */
export function useViewportVisibilitySimple<T extends HTMLElement = HTMLElement>() {
  const { ref, isVisible } = useViewportVisibility<T>({
    threshold: 0.5,
    triggerOnce: true,
    rootMargin: "-10% 0px",
  });

  return { ref, isVisible };
}

/**
 * Optimized hook for animations with device-responsive settings
 * Automatically adjusts thresholds and margins for better cross-device performance
 */
export function useViewportVisibilityForAnimations<
  T extends HTMLElement = HTMLElement,
>() {
  const { ref, isVisible } = useViewportVisibility<T>({
    threshold: 0.4, // Base threshold - will be adjusted per device in main hook
    triggerOnce: true,
    rootMargin: "-8% 0px", // Slightly more generous than simple version
  });

  return { ref, isVisible };
}

export default useViewportVisibility;
